// Browser-safe file calculation adapter.
//
// Wraps the existing core-engine JSON / CSV / xlsx parsers and
// calculation logic so the React UI can call them without importing
// any Node-only modules. No calculation rules are duplicated here —
// the adapter is a thin shim that maps File / ArrayBuffer / string
// into the existing engine functions.

import {
  calculateNewSupplyLandPrice,
  calculateChangePremium,
  calculateExcessGfaPremium,
  calculateTemporaryUseFee,
  calculateKeyIndustrialLeaseAnnualRent,
  calculateIndustrialCapacityIncreasePremium,
  type CalculateNewSupplyInput,
  type TemporaryUseFeeInput,
} from "../../logic/index.js";
import type { ChangePremiumInput } from "../../types/change-premium.js";
import type { ExcessGfaInput } from "../../logic/excess-gfa.js";
import type { CalculationResult } from "../../types/calculation-result.js";
import type { ChangePremiumResult } from "../../types/change-premium.js";
import type { TemporaryUseFeeResult } from "../../logic/temporary-use.js";
import type { ExcessGfaResult } from "../../logic/excess-gfa.js";
import type { KeyIndustrialLeaseAnnualRentResult } from "../../types/lease-rent.js";
import type { IndustrialCapacityIncreaseResult } from "../../types/capacity-increase.js";

import { parseDemoJson } from "../../demo/input.js";
import { parseNewSupplyCsv } from "../../demo/csv-input.js";
import { parseNewSupplyXlsx } from "../../demo/xlsx-input.js";
import type { ValidationMessage } from "../../types/validation.js";
import type { MarketPriceSet } from "../../types/market-price.js";

/** File extension → branch discriminator. */
export type FileKind = "json" | "csv" | "xlsx";

export function inferFileKind(name: string): FileKind {
  const lower = name.toLowerCase();
  if (lower.endsWith(".json")) return "json";
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "xlsx";
  throw new Error(`不支持的文件格式: ${name}（仅支持 .json / .csv / .xlsx）`);
}

// ---------------------------------------------------------------------------
// Result envelope — what the UI renders
// ---------------------------------------------------------------------------

export type CalculationTypeLabel =
  | "新供应土地"
  | "变更补缴"
  | "竣工超面积"
  | "临时用地使用费"
  | "短期租赁"
  | "重点产业项目租赁年租金"
  | "产业用地提高容积"
  | "批量测算"
  | "未知";

export interface RunResult {
  readonly calculationType: CalculationTypeLabel;
  readonly calculationTypeRaw: string;
  readonly projectName?: string;
  readonly calculationDate?: string;
  readonly marketPrices?: MarketPriceSet;
  readonly validationMessages: readonly ValidationMessage[];

  // Optional: surfaced for a single new-supply-style calculation.
  readonly totalLandPrice?: number;
  readonly totalChargeableGfa?: number;
  readonly averageFloorLandPrice?: number;

  // Optional: change-premium / excess-gfa / capacity-increase.
  readonly premium?: number;
  readonly beforeResult?: CalculationResult;
  readonly afterResult?: CalculationResult;

  // Optional: temporary-use / short-term-lease.
  readonly totalFee?: number;
  readonly kindLabel?: "临时用地使用费" | "短期租赁";

  // Optional: lease-rent.
  readonly twentyYearLandPrice?: number;
  readonly annualRent?: number;

  // Optional: capacity-increase.
  readonly capacityIncreasePremium?: number;

  // Always present for the per-item table.
  readonly itemResults?: readonly CalculationResult["itemResults"][number][];

  // Optional: free-form note from the engine.
  readonly note?: string;
}

export interface RunFileOutput {
  readonly result: RunResult;
  readonly errors: readonly ValidationMessage[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function runFile(
  name: string,
  data: ArrayBuffer | string,
): Promise<RunFileOutput> {
  const kind = inferFileKind(name);
  const collected: ValidationMessage[] = [];

  if (kind === "json") {
    const text =
      typeof data === "string" ? data : new TextDecoder("utf-8").decode(data);
    return runJsonText(text, name, collected);
  }
  if (kind === "csv") {
    const text =
      typeof data === "string" ? data : new TextDecoder("utf-8").decode(data);
    return runCsvText(text, name, collected);
  }
  // xlsx
  const buf = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
  return runXlsxBuffer(buf, name, collected);
}

// ---------------------------------------------------------------------------
// JSON branch
// ---------------------------------------------------------------------------

function runJsonText(
  text: string,
  source: string,
  collected: ValidationMessage[],
): RunFileOutput {
  // Parse the file to discover the calculationType first, then dispatch
  // to the matching engine function.
  const file = parseDemoJson(text, source);

  switch (file.calculationType) {
    case "new-supply": {
      const r = runNewSupply(file.input);
      collected.push(...r.result.validationMessages);
      return r;
    }
    case "change-premium": {
      const r = runChangePremium(file.input);
      collected.push(...r.result.validationMessages ?? []);
      return r;
    }
    case "excess-gfa": {
      const r = runExcessGfa(file.input);
      collected.push(...r.result.validationMessages ?? []);
      return r;
    }
    case "temporary-use":
    case "short-term-lease": {
      const r = runTemporaryUse(file.input, file.calculationType);
      collected.push(...r.result.validationMessages ?? []);
      return r;
    }
    case "lease-rent": {
      const r = runLeaseRent(file.input);
      collected.push(...r.result.validationMessages ?? []);
      return r;
    }
    case "capacity-increase": {
      const r = runCapacityIncrease(file.input);
      collected.push(...r.result.validationMessages ?? []);
      return r;
    }
    case "batch": {
      // The batch branch is intentionally narrow: we run each item
      // through its engine and aggregate the totals.
      const errors: ValidationMessage[] = [];
      let totalLandPrice = 0;
      let totalPremium = 0;
      let messages: ValidationMessage[] = [];
      file.items.forEach((it, idx) => {
        const inner = it.file;
        try {
          if (inner.calculationType === "new-supply") {
            const out = runNewSupply(inner.input);
            totalLandPrice += out.result.totalLandPrice ?? 0;
            messages = messages.concat([...out.result.validationMessages]);
          } else {
            const out = runChangePremium(inner.input);
            totalLandPrice += out.result.afterResult?.totalLandPrice ?? 0;
            totalPremium += out.result.premium ?? 0;
            messages = messages.concat([
              ...(out.result.afterResult?.validationMessages ?? []),
              ...(out.result.beforeResult?.validationMessages ?? []),
            ]);
          }
        } catch (e) {
          errors.push({
            level: "error",
            field: `items[${idx}]`,
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
      collected.push(...errors, ...messages);
      return {
        result: {
          calculationType: "批量测算",
          calculationTypeRaw: "batch",
          totalLandPrice,
          premium: totalPremium,
          validationMessages: messages,
        },
        errors,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// CSV branch (new-supply only)
// ---------------------------------------------------------------------------

function runCsvText(
  text: string,
  source: string,
  collected: ValidationMessage[],
): RunFileOutput {
  const parsed = parseNewSupplyCsv(text);
  if (parsed.errors.length > 0 || !parsed.value) {
    collected.push(...parsed.errors);
    return {
      result: {
        calculationType: "未知",
        calculationTypeRaw: "csv-new-supply",
        validationMessages: parsed.errors,
      },
      errors: parsed.errors,
    };
  }
  const out = runNewSupply(parsed.value.input);
  collected.push(...out.result.validationMessages);
  return out;
}

// ---------------------------------------------------------------------------
// xlsx branch (new-supply only)
// ---------------------------------------------------------------------------

async function runXlsxBuffer(
  buf: Uint8Array,
  source: string,
  collected: ValidationMessage[],
): Promise<RunFileOutput> {
  const parsed = await parseNewSupplyXlsx(buf);
  if (parsed.errors.length > 0 || !parsed.value) {
    collected.push(...parsed.errors);
    return {
      result: {
        calculationType: "未知",
        calculationTypeRaw: "xlsx-new-supply",
        validationMessages: parsed.errors,
      },
      errors: parsed.errors,
    };
  }
  const out = runNewSupply(parsed.value.input);
  collected.push(...out.result.validationMessages);
  return out;
}

// ---------------------------------------------------------------------------
// Per-type engine wrappers
// ---------------------------------------------------------------------------

function runNewSupply(input: CalculateNewSupplyInput): RunFileOutput {
  const r: CalculationResult = calculateNewSupplyLandPrice(input);
  return {
    result: {
      calculationType: "新供应土地",
      calculationTypeRaw: "new-supply",
      projectName: input.parcel.projectName,
      calculationDate: input.parcel.calculationDate,
      marketPrices: input.prices,
      totalLandPrice: r.totalLandPrice,
      totalChargeableGfa: r.totalChargeableGfa,
      averageFloorLandPrice: r.averageFloorLandPrice,
      itemResults: r.itemResults,
      validationMessages: r.validationMessages,
    },
    errors: [],
  };
}

function runChangePremium(input: ChangePremiumInput): RunFileOutput {
  const r: ChangePremiumResult = calculateChangePremium(input);
  return {
    result: {
      calculationType: "变更补缴",
      calculationTypeRaw: "change-premium",
      premium: r.premium,
      beforeResult: r.beforeResult,
      afterResult: r.afterResult,
      itemResults: r.afterResult.itemResults,
      validationMessages: [
        ...r.beforeResult.validationMessages,
        ...r.afterResult.validationMessages,
      ],
    },
    errors: [],
  };
}

function runExcessGfa(input: ExcessGfaInput): RunFileOutput {
  const r: ExcessGfaResult = calculateExcessGfaPremium(input);
  return {
    result: {
      calculationType: "竣工超面积",
      calculationTypeRaw: "excess-gfa",
      premium: r.excessGfaPremium,
      beforeResult: r.base.beforeResult,
      afterResult: r.base.afterResult,
      itemResults: r.base.afterResult.itemResults,
      validationMessages: [
        ...r.base.beforeResult.validationMessages,
        ...r.base.afterResult.validationMessages,
      ],
      note: r.note,
    },
    errors: [],
  };
}

function runTemporaryUse(
  input: TemporaryUseFeeInput,
  kind: "temporary-use" | "short-term-lease",
): RunFileOutput {
  const r: TemporaryUseFeeResult = calculateTemporaryUseFee(input);
  return {
    result: {
      calculationType:
        kind === "short-term-lease" ? "短期租赁" : "临时用地使用费",
      calculationTypeRaw: kind,
      projectName: input.parcel.projectName,
      calculationDate: input.parcel.calculationDate,
      marketPrices: input.prices,
      totalFee: r.totalFee,
      totalLandPrice: r.baseResult.totalLandPrice,
      totalChargeableGfa: r.baseResult.totalChargeableGfa,
      averageFloorLandPrice: r.baseResult.averageFloorLandPrice,
      itemResults: r.baseResult.itemResults,
      kindLabel: kind === "short-term-lease" ? "短期租赁" : "临时用地使用费",
      validationMessages: r.baseResult.validationMessages,
      note: r.note,
    },
    errors: [],
  };
}

function runLeaseRent(input: CalculateNewSupplyInput): RunFileOutput {
  const r: KeyIndustrialLeaseAnnualRentResult =
    calculateKeyIndustrialLeaseAnnualRent(input);
  return {
    result: {
      calculationType: "重点产业项目租赁年租金",
      calculationTypeRaw: "lease-rent",
      projectName: input.parcel.projectName,
      calculationDate: input.parcel.calculationDate,
      marketPrices: input.prices,
      twentyYearLandPrice: r.twentyYearLandPrice,
      annualRent: r.annualRent,
      itemResults: r.baseResult.itemResults,
      totalChargeableGfa: r.baseResult.totalChargeableGfa,
      validationMessages: r.baseResult.validationMessages,
      note: r.note,
    },
    errors: [],
  };
}

function runCapacityIncrease(
  input: ChangePremiumInput & { mainIndustrialLandType: "M1" | "M0" | "W1" | "W0" },
): RunFileOutput {
  const r: IndustrialCapacityIncreaseResult =
    calculateIndustrialCapacityIncreasePremium(input);
  return {
    result: {
      calculationType: "产业用地提高容积",
      calculationTypeRaw: "capacity-increase",
      premium: r.capacityIncreasePremium,
      beforeResult: r.base.beforeResult,
      afterResult: r.base.afterResult,
      capacityIncreasePremium: r.capacityIncreasePremium,
      itemResults: r.base.afterResult.itemResults,
      validationMessages: [
        ...r.base.beforeResult.validationMessages,
        ...r.base.afterResult.validationMessages,
      ],
    },
    errors: [],
  };
}
